import React, { forwardRef, } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as icons from '@boxine/tonies-ui/icons';
import { Message } from '../components/Message';
import Icon from '../../../../components/Icon';
import { FocusStyles } from '../../../../styles/FocusStyles';
import { Spacing } from '../../../../components/Spacing';
import { AssistiveTechnologyOnly } from '../../../../styles/AssistiveTechnologyOnly';
export const DisabledWrapper = styled.div `
  ${({ isDisabled }) => isDisabled &&
    `
          cursor: not-allowed;
        `}
`;
export const CheckboxWrapper = styled.div `
  display: inline-flex;
  flex-direction: column;
  margin: 0.25em 0;
  position: relative;
  width: fit-content;
  border-radius: 8px;
`;
const StyledIcon = styled(Icon) `
  position: absolute;
  pointer-events: none;
  @keyframes attention-checkmark {
    50% {
      transform: scale(1.4);
    }
    100% {
      transform: scale(1);
    }
  }
  animation: attention-checkmark 150ms linear;

  left: 0.125rem;
  top: 0.125rem;
`;
export const FieldWrapper = styled.div `
  display: inline-block;
  grid-area: checkbox;

  > input {
    appearance: none;
    background-color: ${({ isChecked, styling, theme }) => isChecked && styling !== 'secondary'
    ? theme.colors['green-100']
    : styling === 'secondary' && isChecked
        ? theme.colors['white']
        : 'transparent'};
    border-color: ${({ status, isDisabled, isChecked, styling, theme }) => status === 'error' && styling !== 'secondary' && !isDisabled
    ? theme.colors.primary
    : isChecked && styling !== 'secondary'
        ? theme.colors['green-100']
        : styling === 'secondary' && !isDisabled
            ? theme.colors['white']
            : isDisabled && styling !== 'secondary'
                ? theme.colors['normalgrey']
                : isDisabled && styling === 'secondary'
                    ? theme.colors['white-65']
                    : theme.colors['darkergrey']};
    border-style: solid;
    border-width: ${({ size }) => size === 'small' ? '0.0825rem' : '0.125rem'};
    border-radius: ${({ size }) => (size === 'small' ? '0.25rem' : '0.375rem')};
    box-shadow: ${({ styling, theme }) => styling === 'secondary'
    ? `0 0 0.0625rem ${theme.colors['white']}`
    : '0 0 0.0625rem transparent'};
    display: block;
    font-family: ${props => props.theme.fonts.serif};
    margin: 0;
    padding: 0;
    width: ${({ size }) => (size === 'small' ? '1rem' : '1.5rem')};
    height: ${({ size }) => (size === 'small' ? '1rem' : '1.5rem')};

    &:focus,
    &:focus-visible,
    &.focus-visible {
      ${FocusStyles}
    }

    &::placeholder {
      opacity: 0;
    }

    ${({ isDisabled }) => isDisabled ? 'cursor: not-allowed' : 'cursor: pointer'};
  }

  > svg {
    pointer-events: none;
    position: absolute;
    left: 2px;
    top: 2px;
    width: ${({ size }) => (size === 'small' ? '0.75rem' : '1.25rem')};
    height: ${({ size }) => (size === 'small' ? '0.75rem' : '1.25rem')};
  }
`;
export const Label = styled.label `
  cursor: inherit;
  background-color: transparent;
  display: grid;
  grid-template-areas:
    'checkbox text'
    'message message';
  grid-template-columns: ${({ size }) => size === 'small' ? '0.5rem auto' : '1rem auto'};
  font-family: ${props => props.theme.fonts.serif};
  font-size: 0.8125rem;
  font-weight: 300;
  line-height: 1.385;
  text-align: left;
  transition: all 0.25s ease-in-out;
  height: auto;
  width: 100%;
  align-items: start;
  border-radius: 0.5rem 0 0 0.5rem;
  padding: 0;
  margin: 0;
  white-space: no-wrap;

  > span {
    color: ${({ styling, isDisabled, theme }) => {
    if (styling === 'secondary') {
        return theme.colors['white'];
    }
    if (isDisabled) {
        return styling === 'primary'
            ? theme.colors['normalgrey']
            : theme.colors['white-65'];
    }
    return theme.colors['darkergrey'];
}};

    ${props => props.isLabelHidden &&
    css `
        ${AssistiveTechnologyOnly};
      `};
  }
`;
export const TextWrapper = styled.span `
  grid-area: text;
  padding: ${({ size }) => size === 'small' ? '0.215rem 0 0 1.25rem' : '0.5rem 0 0 1.25rem'};
  width: fit-content;
  height: fit-content;
`;
export const MessageWrapper = styled.span `
  grid-area: message;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  height: fit-content;
`;
const CheckboxInner = ({ dataTestIdCheckbox = 'checkbox', dataTestIdLabel = 'checkbox-label', dataTestIdMessage = 'checkbox-message', hint = undefined, isDisabled = false, isOptional = false, label = undefined, message = undefined, name, onBlur = () => undefined, onChange = () => undefined, onClick = () => undefined, onKeyDown = () => undefined, status = undefined, isChecked, size = 'default', styling: stylingProp = undefined, isLabelHidden = false, }, ref
// eslint-disable-next-line sonarjs/cognitive-complexity
) => {
    const { t } = useTranslation();
    const checkboxId = `checkbox-id-${name}`;
    const hintId = `hint-id-${name}`;
    const messageId = `message-id-${name}`;
    const theme = useTheme();
    const styling = stylingProp || (theme.colorContext === 'dark' ? 'secondary' : 'primary');
    return (React.createElement(Label, { "data-testid": dataTestIdLabel, htmlFor: checkboxId, size: size, isDisabled: isDisabled, styling: styling, isLabelHidden: isLabelHidden },
        React.createElement(DisabledWrapper, { "data-testid": "checkbox-disabled-wrapper", isDisabled: isDisabled },
            React.createElement(CheckboxWrapper, { "data-testid": "checkbox-wrapper" },
                React.createElement(FieldWrapper, { "data-testid": "checkbox-field-wrapper", isDisabled: isDisabled, status: status, size: size, isChecked: isChecked, styling: styling },
                    React.createElement("input", { onBlur: onBlur, tabIndex: 0, "aria-describedby": (message === null || message === void 0 ? void 0 : message.text) && !message.ariaLive ? messageId : '', "aria-invalid": status === 'error', "data-testid": dataTestIdCheckbox, disabled: isDisabled, id: checkboxId, name: name, onChange: onChange, onClick: onClick, onKeyDown: onKeyDown, ref: ref, required: !isOptional, type: "checkbox", checked: isChecked }),
                    isChecked && (React.createElement(StyledIcon, { type: icons.checkmark, fill: styling === 'secondary'
                            ? theme.colors['darkergrey']
                            : theme.colors['white'] }))))),
        React.createElement(TextWrapper, { size: size },
            label,
            ' ',
            isOptional && (React.createElement("span", { "data-testid": "checkbox-label-optional" },
                "(",
                t('InputField:Optional'),
                ")"))),
        message && (React.createElement(MessageWrapper, null,
            React.createElement(Message, { message: message, status: status, dataTestId: dataTestIdMessage, id: messageId, styling: styling }))),
        hint && message && (React.createElement(MessageWrapper, null,
            React.createElement(Spacing, { pt: "spacing-s" },
                React.createElement(Message, { message: { text: '* ' + hint }, dataTestId: "checkbox-hint", id: hintId, styling: styling })))),
        hint && !message && (React.createElement(MessageWrapper, null,
            React.createElement(Message, { message: { text: '* ' + hint }, dataTestId: "checkbox-hint", id: hintId, styling: styling })))));
};
export const Checkbox = forwardRef(CheckboxInner);
Checkbox.displayName = 'Checkbox';
